import React, { FC, HTMLAttributes } from "react";
import styled from "styled-components";
import { Box, Tab, TabContent } from "../TabView";
import { CenterParagraph, ListItem, Note, UList, CenterNote, Breakable } from "./Literals";
import ResolutionImg from "../image/ResolutionImg";
import { BORDER_RADIUS } from "../calculation/theme";

const CONCEPT_COLOR = "#4EB75A";

export const TaxReturnTab: FC<HTMLAttributes<HTMLDivElement> & { active: boolean }> = ({ active, ...props }) => {
  return (
    <ColoredTab active={active} {...props}>
      <Breakable>確定申告を</Breakable>
      <Breakable>する場合</Breakable>
    </ColoredTab>
  );
};
const ColoredTab = styled(Tab)`
  color: ${({ active }) => (active ? "#FFFFFF" : "#1a1a1a")};
  background-color: ${({ active }) => (active ? CONCEPT_COLOR : "#CCCCCC")};
  padding: 10px 40px;
`;

const TaxReturnManual: FC<{ active: boolean }> = ({ active }) => {
  return (
    <Root active={active}>
      <BoxTarget subject="対象者" color={CONCEPT_COLOR}>
        <BoxInner>
          <StyledUList>
            <ListItem>
              年間の自治体への寄附が<span style={{ color: "#EB0000" }}>６箇所以上</span>の場合
            </ListItem>
            <ListItem>
              他に確定申告が必要な事案が<span style={{ color: "#EB0000" }}>ある</span>
            </ListItem>
          </StyledUList>
          <Note>同じ自治体であれば複数寄附しても１箇所として計算します。</Note>
          <Note>年収2,000万を超える所得者、医療費控除などで確定申告が必要な方はこちらが対象になります。</Note>
        </BoxInner>
      </BoxTarget>
      <BoxSystemFlow>
        <StyledBox subject="控除の仕組み" color={CONCEPT_COLOR}>
          <FlowImage pc="/flow/tax-return/flow-pc.png" sp="/flow/tax-return/flow-sp.png" loading="lazy" />
        </StyledBox>
        <StyledBox subject="確定申告の流れ" color={CONCEPT_COLOR}>
          <FlowImage pc="/flow/tax-return/schedule-pc.png" sp="/flow/tax-return/schedule-sp.png" loading="lazy" />
        </StyledBox>
      </BoxSystemFlow>
      <BoxNecessity subject="申請に必要なもの" color={CONCEPT_COLOR}>
        <CenterParagraph>
          確定申告の書類を作成し、
          <br />
          期日までに税務署に郵送か持参する必要があります。
        </CenterParagraph>
        <NecessitiesBox>
          <NecessitiesTitle>＜通常の確定申告に必要なもの＞</NecessitiesTitle>
          <NecessitiesList>
            <Necessity>
              <NecessitiesImage pc="/flow/tax-return/necessities-1-pc.png" sp="/flow/tax-return/necessities-1-sp.png" loading="lazy" />
              <NecessityName>対象期間の源泉徴収票</NecessityName>
            </Necessity>
            <Necessity>
              <NecessitiesImage pc="/flow/tax-return/necessities-2-pc.png" sp="/flow/tax-return/necessities-2-sp.png" loading="lazy" />
              <NecessityName>印鑑</NecessityName>
            </Necessity>
            <Necessity>
              <NecessitiesImage pc="/flow/tax-return/necessities-3-pc.png" sp="/flow/tax-return/necessities-3-sp.png" loading="lazy" />
              <NecessityName>還付金受け取り用口座番号</NecessityName>
            </Necessity>
            <Necessity>
              <NecessitiesImage pc="/flow/tax-return/necessities-4-pc.png" sp="/flow/tax-return/necessities-4-sp.png" loading="lazy" />
              <NecessityName>指定の本人確認書類</NecessityName>
            </Necessity>
          </NecessitiesList>
        </NecessitiesBox>
        <div style={{ fontSize: "40px", textAlign: "center", margin: "10px 0" }}>+</div>
        <Necessity2>
          <NecessitiesImage2 pc="/flow/tax-return/necessities-5-pc.png" sp="/flow/tax-return/necessities-5-sp.png" loading="lazy" />
          <NecessityName>対象期間全ての寄附金受領証明書</NecessityName>
          <CenterNote>返礼品とともに配送されます。</CenterNote>
        </Necessity2>
        <TaxAgencyLink href="https://www.nta.go.jp/taxes/shiraberu/shinkoku/tokushu/" target="_blank">
          <div>確定申告の書き方については<br />国税庁のサイトを参考にしてください。</div>
          <div style={{ fontSize: "0.8em" }}>（外部リンクへ）</div>
        </TaxAgencyLink>
      </BoxNecessity>
    </Root>
  );
};
export default TaxReturnManual;

const Root = styled(TabContent)`
  border: 2px solid ${CONCEPT_COLOR};
  border-radius: 0 0 20px 20px;
  @media screen and (min-width: 961px) {
    padding: 20px 30px;
    display: ${({ active }) => (active ? "flex" : "none")};
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: stretch;
  }
`;
const StyledBox = styled(Box)`
  @media screen and (max-width: 960px) {
    margin-bottom: 30px;
  }
  @media screen and (min-width: 961px) {
    margin-bottom: 40px;
  }
`;
const BoxTarget = styled(StyledBox)`
  margin-top: 20px;
  @media screen and (min-width: 961px) {
    flex-basis: 100%;
  }
`;
const BoxSystemFlow = styled.div`
  @media screen and (min-width: 961px) {
    flex-basis: 49%;
  }
`;
const BoxNecessity = styled(StyledBox)`
  @media screen and (min-width: 961px) {
    flex-basis: 49%;
  }
`;
const BoxInner = styled.div`
  @media screen and (min-width: 961px) {
    width: 420px;
    margin: 0 auto;
  }
`;
const StyledUList = styled(UList)`
  margin: 0 0 10px;
`;
const FlowImage = styled(ResolutionImg)`
  width: 100%;
  @media screen and (max-width: 960px) {
  }
  @media screen and (min-width: 601px) and (max-width: 960px) {
    max-width: 400px;
    margin: 0 auto;
    display: block;
  }
  @media screen and (min-width: 961px) {
    padding: 10px;
  }
`;
const NecessitiesTitle = styled.h6`
  text-align: center;
`;
const NecessitiesBox = styled.div`
  ${BORDER_RADIUS};
  border: 1px solid #c71d2a;
  background-color: #fff5f6;
  padding: 10px;
  @media screen and (min-width: 960px) {
    width: 340px;
    margin: 0 auto;
  }
  @media screen and (min-width: 1441px) {
    width: 420px;
    margin: 0 auto;
  }
`;
const NecessitiesList = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
const Necessity = styled.div`
  font-size: 0.8em;
  flex-basis: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 10px 0;
`;

const NecessitiesImage = styled(ResolutionImg)`
  @media screen and (max-width: 960px) {
    max-height: 60px;
  }
  @media screen and (min-width: 961px) {
    height: 50px;
  }
  @media screen and (min-width: 1441px) {
    height: 60px;
  }
`;
const NecessityName = styled.div`
  margin-top: 10px;
`;
const Necessity2 = styled.div`
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const NecessitiesImage2 = styled(ResolutionImg)`
  @media screen and (max-width: 960px) {
    max-height: 100px;
  }
  @media screen and (min-width: 961px) {
    height: 100px;
  }
  @media screen and (min-width: 1441px) {
    height: 120px;
  }
`;
const TaxAgencyLink = styled.a`
  line-height: 1.428em;
  text-decoration: underline;
  text-align: center;
`;
